import { useEffect, useState } from 'react';
import { envoisApi } from '../../api/resources';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { C } from '../../theme/palette';

/** Pastille de chaque état : [libellé, fond, encre]. */
const ETATS = {
  pret: ['✓ Prêt', C.greenL, C.green],
  actif: ['✓ Actif', C.greenL, C.green],
  packia: ['Pack IA', '#F5EEFE', C.purple],
  partiel: ['Export seulement', C.amberL, C.amber],
  absent: ['Pas encore', C.navyXL, C.grey],
  nonRaccorde: ['Non raccordé', C.navyXL, C.grey],
  inconnu: ['À vérifier', C.navyXL, C.grey],
};

/**
 * État d'un canal d'envoi d'après ce que le serveur dit de son raccordement.
 *
 * @param {boolean|null|undefined} raccorde réponse du serveur, `null` si elle
 *   n'a pas pu être lue (rôle sans accès, réseau)
 * @returns {string} clé d'état
 */
const etatCanal = (raccorde) => (raccorde == null ? 'inconnu' : raccorde ? 'pret' : 'nonRaccorde');

/**
 * Les connexions de SwissTech et leur état réel dans cette installation.
 *
 * La maquette V288 présente ces intégrations comme prêtes ou « à la mise en
 * ligne ». La production est en ligne : chaque ligne dit donc ce qui marche
 * aujourd'hui, et seulement cela. Une connexion qui n'existe pas encore
 * l'annonce ; un export qui n'est pas une transmission ne se dit pas
 * transmission. Les envois e-mail et SMS dépendent du raccordement du
 * serveur, que les réglages d'envoi rapportent.
 *
 * @param {object} props propriétés du composant
 * @param {boolean} props.iaActif vrai quand le Pack IA est souscrit
 * @param {() => Promise<object>} [props.chargeEnvois] lecture des réglages
 *   d'envoi (raccordements SMTP et SMS)
 * @returns {import('react').ReactElement} la carte des intégrations
 */
export function IntegrationsCard({ iaActif, chargeEnvois = () => envoisApi.reglages() }) {
  const [canaux, setCanaux] = useState({ courriel: undefined, sms: undefined });
  useEffect(() => {
    let vivant = true;
    Promise.resolve()
      .then(chargeEnvois)
      .then((r) => {
        if (vivant)
          setCanaux({ courriel: r?.courrielConfigure ?? null, sms: r?.smsConfigure ?? null });
      })
      .catch(() => {
        if (vivant) setCanaux({ courriel: null, sms: null });
      });
    return () => {
      vivant = false;
    };
    // La lecture se fait une fois, à l'ouverture de la page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lignes = [
    [
      '🏦',
      'Relevé bancaire camt.053',
      'Relevé de ta banque (Compta) ou camt.054 (Factures) : paiements rapprochés des factures',
      'pret',
    ],
    [
      '🔗',
      'Connexion bancaire directe',
      'Synchronisation automatique (UBS, PostFinance, Raiffeisen, BCV…) ; le relevé camt.053 en tient lieu',
      'absent',
    ],
    [
      '🤖',
      'IA — lecture auto & dictée',
      'Factures, dépenses et devis remplis tout seuls',
      iaActif ? 'actif' : 'packia',
    ],
    [
      '📤',
      'Transmission au fiduciaire',
      'Export comptable (CSV) + accès lecture pour ton comptable, sans envoi automatique',
      'pret',
    ],
    [
      '💼',
      'SwissDec (salaires)',
      'XML Swissdec ELM 5 à télécharger, ni certifié ni transmis aux assurances et impôts',
      'partiel',
    ],
    [
      '📁',
      'Archivage des pièces',
      'Pièces émises conservées 15 ans, inaltérables (sans certification Olico)',
      'pret',
    ],
    [
      '📧',
      'Envoi automatique par e-mail',
      canaux.courriel === false
        ? 'Aucun serveur d’envoi n’est raccordé : rappels gardés au journal'
        : 'Devis, factures et rappels envoyés automatiquement',
      canaux.courriel === undefined ? 'inconnu' : etatCanal(canaux.courriel),
    ],
    [
      '💬',
      'Envoi automatique par SMS',
      canaux.sms === false
        ? 'Aucun prestataire SMS n’est raccordé : SMS gardés au journal'
        : 'Rappels de rendez-vous et de paiement par SMS',
      canaux.sms === undefined ? 'inconnu' : etatCanal(canaux.sms),
    ],
  ];

  return (
    <Card style={{ marginTop: 12, marginBottom: 8 }}>
      <Bar
        icon="🔌"
        title="Intégrations & Marketplace"
        color={C.teal}
        sub="Ce qui est raccordé aujourd’hui"
      />
      <ul
        aria-label="Intégrations"
        style={{ listStyle: 'none', margin: 0, padding: '13px 13px 0' }}
      >
        {lignes.map(([icone, titre, description, etat]) => {
          const [libelle, fond, encre] = ETATS[etat];
          return (
            <li
              key={titre}
              data-etat={etat}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 11,
                padding: '9px 0',
                borderBottom: '1px solid ' + C.border,
              }}
            >
              <span
                aria-hidden="true"
                style={{ fontSize: 18, width: 26, textAlign: 'center', flexShrink: 0 }}
              >
                {icone}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 800 }}>{titre}</div>
                <div style={{ fontSize: 8.5, color: C.muted, lineHeight: 1.4 }}>{description}</div>
              </div>
              <span
                style={{
                  fontSize: 8,
                  fontWeight: 800,
                  padding: '3px 8px',
                  borderRadius: 20,
                  whiteSpace: 'nowrap',
                  background: fond,
                  color: encre,
                  flexShrink: 0,
                }}
              >
                {libelle}
              </span>
            </li>
          );
        })}
      </ul>
      <div style={{ fontSize: 8.5, color: C.muted, padding: '0 13px 13px', lineHeight: 1.5 }}>
        États réels de ton installation. « Pas encore » n’existe pas aujourd’hui ; « Non raccordé »
        attend un réglage du serveur. Rien ne s’active à ton insu.
      </div>
    </Card>
  );
}
